/**
 * The two static move-in info PDFs, as the dashboard reads them (COL-533).
 *
 * These are the same objects `tenant-lifecycle-email-tick` attaches to the
 * T-1 move-in email — one source file, two readers. The edge function reads
 * them with the service role; the dashboard signs them in the browser as the
 * tenant, which is what
 * `20261021190514_tenant_read_email_attachment_templates.sql` allows.
 *
 * MIRRORS `STATIC_ATTACHMENTS` in
 * supabase/functions/tenant-lifecycle-email-tick/index.ts — Vite and Deno
 * cannot share a module, so the paths are written twice and
 * tenant-house-documents.test.ts reads the edge function's source to assert
 * the two lists have not drifted apart.
 *
 * Constants only, deliberately: the opener lives in `tenant-document-url.ts`
 * so that importing this list does not construct a Supabase client, and the
 * drift test above can run as a plain unit test.
 */

import { businessToday } from "@/lib/businessDate";

/** The bucket the move-in templates live in. */
export const TEMPLATE_BUCKET = "templates";

/**
 * The folder the tenant may read. Everything else in `templates` — the
 * contract and Wohnungsgeberbestätigung blanks — stays service-role only.
 */
export const TEMPLATE_ATTACHMENT_FOLDER = "email-attachments";

/**
 * How many days before move-in the two PDFs become visible.
 *
 * Neel, in the COL-533 thread: "both should only become available via the
 * dashboard the same day/time the email gets sent." The T-1 move-in email
 * opens its window at `move_in_details: { from: -1 }` in
 * supabase/functions/_shared/lifecycle-emails.ts, so this is that same -1,
 * and the test below reads that file to make sure the two stay equal.
 */
export const HOUSE_DOCUMENTS_DAYS_BEFORE_MOVE_IN = 1;

/** Calendar-date math on `YYYY-MM-DD`, in UTC so no DST shift can creep in. */
function shiftIsoDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/**
 * Whether the two explainers are open to this tenant yet.
 *
 * Compared as business calendar days (`businessToday`), not against a UTC
 * timestamp: `bookings.move_in_date` is a plain DATE meaning a day in
 * Germany, and for part of every day UTC is a different day — which would
 * release the documents a day early or late depending on the hour.
 *
 * No move-in date means no email is scheduled either, so nothing is
 * released: the tenant sees the same "from move-in" pill as before.
 */
export function houseDocumentsReleased(
  moveInDate: string | null | undefined,
  today: string = businessToday()
): boolean {
  const iso = (moveInDate ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  return today >= shiftIsoDays(iso, -HOUSE_DOCUMENTS_DAYS_BEFORE_MOVE_IN);
}

export type HouseDocumentKey = "wasteSorting" | "broadcastingFee";

export const HOUSE_INFO_DOCUMENTS: ReadonlyArray<{
  key: HouseDocumentKey;
  path: string;
}> = [
  {
    key: "wasteSorting",
    path: `${TEMPLATE_ATTACHMENT_FOLDER}/muelltrennung-waste-sorting.pdf`,
  },
  {
    key: "broadcastingFee",
    path: `${TEMPLATE_ATTACHMENT_FOLDER}/rundfunkbeitrag-broadcasting-fee.pdf`,
  },
];
